<template>
  <fieldset class="product-option" v-if="items.length">
    <legend class="mb-10 text-base max-sm:text-sm font-medium text-[#656667]">
      {{ label }}:
      <span >{{ activeLabel }}</span>
    </legend>

    <div class="flex flex-wrap gap-5">
      <button
        v-for="item in items"
        :key="item.handle"
        type="button"
        class="plp-flavor-swatch p-[4px]"
        :class="{
          'plp-flavor-swatch--active': activeHandle === item.handle,
          'opacity-60 pointer-events-none': loading,
        }"
        :style="
          item.color && activeHandle === item.handle
            ? {backgroundColor: item.color}
            : {}
        "
        :title="item.label"
        :aria-current="activeHandle === item.handle ? 'true' : null"
        @click.prevent="select(item)"
      >
        <img
          v-if="item.icon"
          :src="item.icon"
          :alt="item.label"
          class="w-full h-full object-contain rounded-full"
          :class="{'brightness-0 invert': activeHandle === item.handle}"
          loading="lazy"
        >
      </button>
    </div>
  </fieldset>
</template>

<script>
import {ref, computed} from "vue";
import JSON5 from "json5";

// Section IDs in templates/product.json — swapped on linked-product change.
const SECTION_IDS = ["main", "upgraded", "ingredients", "reviews"];

export default {
  name: "LinkedProductSelector",
  props: {
    items: {
      type: Array,
      default: () => [],
    },
    label: {
      type: String,
      default: "",
    },
    currentHandle: {
      type: String,
      required: true,
    },
    rawItems: {
      type: String,
      default: "",
    },
  },
  setup(props) {
    const activeHandle = ref(props.currentHandle);
    const loading = ref(false);

    // Allow passing items as a JSON5 string from Liquid (avoids :items binding
    // quote-escaping headaches).
    const parsedItems = props.rawItems
      ? JSON5.parse(props.rawItems)
      : props.items;

    const resolvedItems = ref(parsedItems || []);

    const activeLabel = computed(() => {
      const current = resolvedItems.value.find(
        i => i.handle === activeHandle.value,
      );
      return current?.label || "";
    });

    const remountVue = root => {
      const factory = window.__arkCreateVueApp;
      if (!factory) return;
      const vueEls = root.querySelectorAll("[vue]");
      vueEls.forEach(el => {
        try {
          factory().mount(el);
        } catch (err) {
          console.error("[LinkedProductSelector] re-mount failed", err);
        }
      });
    };

    const swapFromHtml = async handle => {
      const res = await fetch(`/products/${handle}`, {
        headers: {Accept: "text/html"},
      });
      if (!res.ok) throw new Error(`Fetch failed: ${res.status}`);
      const html = await res.text();
      const doc = new DOMParser().parseFromString(html, "text/html");

      // Update document title.
      const newTitle = doc.querySelector("title");
      if (newTitle) document.title = newTitle.textContent;

      // Replace each known section's inner HTML with the matching node from
      // the fetched page. Fall back to searching any `shopify-section-*`
      // element if an ID match fails, then remount Vue on each updated node.
      let swapped = 0;
      SECTION_IDS.forEach(id => {
        const selector = `#shopify-section-${id}`;
        const newEl = doc.querySelector(selector);
        const oldEl = document.querySelector(selector);
        if (newEl && oldEl) {
          oldEl.innerHTML = newEl.innerHTML;
          remountVue(oldEl);
          swapped += 1;
        }
      });

      if (swapped === 0) {
        // No known section IDs matched — swap every top-level section wrapper
        // instead, indexed by position.
        const oldSections = document.querySelectorAll("[id^='shopify-section-']");
        const newSections = doc.querySelectorAll("[id^='shopify-section-']");
        oldSections.forEach(oldEl => {
          const match = Array.from(newSections).find(n => n.id === oldEl.id);
          if (match) {
            oldEl.innerHTML = match.innerHTML;
            remountVue(oldEl);
            swapped += 1;
          }
        });
      }

      if (swapped === 0) {
        throw new Error("No sections were updated");
      }
    };

    const select = async item => {
      if (loading.value || item.handle === activeHandle.value) return;
      loading.value = true;
      // Update the URL *before* swapping so newly-mounted Vue components read
      // the fresh `location.search` (no stale `?variant=X` from the previous
      // product, which would crash useProductForm.onMounted).
      history.pushState({}, "", `/products/${item.handle}`);
      try {
        await swapFromHtml(item.handle);
        activeHandle.value = item.handle;
        window.scrollTo({top: 0, behavior: "smooth"});
      } catch (err) {
        console.error("[LinkedProductSelector] swap failed", err);
        window.location.href = `/products/${item.handle}`;
      } finally {
        loading.value = false;
      }
    };

    return {
      activeHandle,
      loading,
      items: resolvedItems,
      activeLabel,
      select,
    };
  },
};
</script>
